import z from 'zod'

const _exampleHeadSchema: Head = {
  meta: [
    {
      name: 'twitter:title',
      content: 'Your page title',
    },
    {
      name: 'some-meta',
      content: 'A description of the page',
      id: 'some-id',
      property: 'og',
    },
  ],
  styles: [
    {
      path: '/some-stylesheet.css',
    },
    {
      path: '/some-other-stylesheet.css',
      tagPosition: 'bodyOpen',
    },
  ],
  scripts: [
    {
      path: '/some-script.js',
    },
    {
      path: '/some-other-script.js',
      tagPosition: 'bodyClose',
    },
  ],
  links: [
    {
      rel: 'icon',
      type: 'image/x-icon',
      href: 'https://example.com/favicon.ico',
    },
  ],
}

export type HeadMeta = {
  name?: string
  content?: string
  id?: string
  property?: string
}

export type TagPosition = 'head' | 'bodyClose' | 'bodyOpen'

const tagPositionSchema = z
  .enum(['head', 'bodyClose', 'bodyOpen'])
  .default('head')

export type PathLoadableContent = { path: string; tagPosition?: TagPosition }
export type UrlLoadableContent = { url: string; tagPosition?: TagPosition }

export type LoadableContent = PathLoadableContent | UrlLoadableContent

export const loadableContentSchema = z.union([
  z.object({ path: z.string(), tagPosition: tagPositionSchema.optional() }),
  z.object({ url: z.string(), tagPosition: tagPositionSchema.optional() }),
])

export type HeadLinks = {
  rel?: string
  type?: string
  href?: string
}

export type Head = {
  title?: string
  meta?: HeadMeta[] | Record<string, string>
  styles?: LoadableContent[]
  scripts?: LoadableContent[]
  links?: HeadLinks[]
}

/** Schema for head options. */
export const headSchema = z.object({
  title: z.string().optional().describe('The page title'),
  meta: z
    .union([
      z
        .object({
          name: z.string().min(1).optional(),
          content: z.string().optional(),
          id: z.string().optional(),
          property: z.string().optional(),
        })
        .array(),
      z.record(z.string().min(1), z.string()),
    ])
    .optional()
    .describe(
      'A list of meta tags to be inserted into the page head. Can also be specified as a key, value record for simplicity.',
    ),
  styles: loadableContentSchema
    .array()
    .optional()
    .describe(
      'A list of css file paths/remote urls and other options, used for loading css.',
    ),
  scripts: loadableContentSchema
    .array()
    .optional()
    .describe(
      'A list of js file paths/remote urls and other options, used for loading scripts.',
    ),
  links: z
    .object({
      rel: z.string().optional(),
      type: z.string().optional(),
      href: z.string().optional(),
    })
    .array()
    .optional(),
}) satisfies z.ZodType<Head>
